import { createTheme } from "@mui/material";
declare module "@mui/material/styles" {
  interface Palette {
    tertiary: Palette["primary"];
  }
  interface PaletteOptions {
    tertiary: PaletteOptions["primary"];
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    tertiary: true;
  }
}

const theme = createTheme();

export default createTheme(theme, {
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1920,
    },
  },
  palette: {
    primary: {
      main: "#DD052B",
      contrastText: "#DD052B",
    },
    secondary: {
      main: "#003294",
      contrastText: "#003294",
    },
    tertiary: {
      main: "#006364",
      light: "#19956A",
      contrastText: "#006364",
    },
    text: {
      primary: "#fff",
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          minHeight: "90.9vh",
          borderRadius: "12px",
          boxShadow: "none",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          padding: "16px 32px",
          [theme.breakpoints.down("md")]: {
            minHeight: "80vh",
            maxHeight: "80vh",
            padding: "12px 22px",
          },
        },
        paperWidthMd: {
          minHeight: "80vh",
          maxWidth: "50vw",
          minWidth: "330px",
        },
        paperWidthLg: {
          maxWidth: "54.1vw",
          minWidth: "300px",
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: "#FFF",
          height: "55px",
          width: "55px",
          ":hover": {
            backgroundColor: "#FFF",
            opacity: 0.92,
          },
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        fontSizeLarge: "100px",
      },
    },
    MuiButton: {
      variants: [
        {
          props: { variant: "text", color: "primary" },
          style: {
            fontFamily: "Futura PT",
            fontSize: "12px",
            justifyContent: "space-between",
            textAlign: "start",
            minWidth: "170px",
            minHeight: "30px",
          },
        },
        {
          props: { variant: "contained", color: "tertiary" },
          style: {
            fontFamily: "Futura PT",
            backgroundColor: "#FFF",
            boxShadow: "none",
            paddingLeft: "21px",
            paddingRight: "21px",
            ":hover": {
              backgroundColor: "#FFF",
              boxShadow: "none",
              opacity: 0.95,
            },
          },
        },
        {
          props: { variant: "text", color: "primary" },
          style: {
            fontFamily: "Futura PT",
            fontSize: "12px",
            padding: "15px",
            justifyContent: "space-evenly",
            textAlign: "start",
            minWidth: "200px",
            minHeight: "30px",
          },
        },
        {
          props: { variant: "outlined", color: "primary" },
          style: {
            fontFamily: "Futura PT",
            backgroundColor: "#FFF",
            color: "#DD052B",
            paddingLeft: "21px",
            paddingRight: "16px",
            textAlign: "start",
            justifyContent: "space-around",
            border: "1px solid",
            ":hover": {
              backgroundColor: "#FFF",
              opacity: 0.92,
            },
          },
        },
        {
          props: { variant: "contained", color: "primary" },
          style: {
            fontFamily: "Futura PT",
            backgroundColor: "#FFF",
            boxShadow: "none",
            paddingLeft: "21px",
            paddingRight: "21px",
            ":hover": {
              backgroundColor: "#FFF",
              boxShadow: "none",
              opacity: 0.95,
            },
          },
        },
        {
          props: { variant: "contained", color: "secondary" },
          style: {
            fontFamily: "Futura PT",
            backgroundColor: "#FFF",
            boxShadow: "none",
            paddingLeft: "21px",
            paddingRight: "21px",
            ":hover": {
              backgroundColor: "#FFF",
              boxShadow: "none",
              opacity: 0.95,
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          borderRadius: "32px",
          letterSpacing: "0px",
          fontWeight: "bold",
          lineHeight: "16px",
          fontSize: "14px",
          minHeight: "56px",
          minWidth: "194px",
        },
        iconSizeMedium: {
          "*:first-child": {
            fontSize: "30px",
          },
        },
        sizeLarge: {
          minWidth: "271px",
          minHeight: "77px",
          borderRadius: "40px",
          fontSize: "19px",
          lineHeight: "24px",
          "& svg": {
            fontSize: "40px !important",
          },
        },
        textPrimary: {
          fontFamily: "Futura PT",
          fontSize: "12px",
          justifyContent: "space-between",
          textAlign: "start",
          minWidth: "170px",
          minHeight: "30px",
        },
        containedPrimary: {
          backgroundColor: "#FFF",
          boxShadow: "none",
          paddingLeft: "21px",
          paddingRight: "21px",
          ":hover": {
            backgroundColor: "#FFF",
            boxShadow: "none",
            opacity: 0.95,
          },
        },
        startIcon: {
          "& svg": {
            fontSize: "40px !important",
          },
        },
        text: {
          textTransform: "none",
        },
      },
    },
  },
  typography: {
    fontFamily: "Futura PT",
    subtitle1: { fontFamily: "Futura" },
    subtitle2: { fontFamily: "Futura" },
    body1: {
      fontFamily: "Futura PT",
      lineHeight: "16px",
      letterSpacing: "0px",
      textTransform: "lowercase",
      fontWeight: "normal",
      fontSize: "13px",
    },
    body2: {
      fontFamily: "Futura PT",
      lineHeight: "16px",
      letterSpacing: "0px",
      fontWeight: "bold",
      fontSize: "15px",
    },
    caption: { fontFamily: "Futura PT" },
    h1: {
      fontFamily: "Futura PT",
    },
    h2: {
      fontFamily: "Futura PT",
    },
    h3: {
      fontFamily: "Futura PT",
    },
    h4: {
      fontFamily: "Futura PT",
      textTransform: "none",
      textAlign: "left",
      fontSize: 18,
      lineHeight: "20px",
      width: "100%",
      fontWeight: "bold",
    },
    h5: {
      fontFamily: "Futura PT",
      textTransform: "none",
      fontSize: 30,
      lineHeight: "1.1em",
      width: "100%",
      textAlign: "center",
    },
    h6: {
      fontFamily: "Futura PT",
      fontWeight: 400,
    },
    text: {
      fontFamily: "Futura PT",
    },
  },
});
